import React, { useState } from 'react';
import { stats } from '../mock/mock';
import { submitLead } from '../lib/leadSubmit';
import { ShieldCheck, CheckCircle2, Zap } from 'lucide-react';
import { Button } from './ui/button';

const Hero = () => {
  const [category, setCategory] = useState('Health');
  const [phone, setPhone] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [sent, setSent] = useState(false);

  const handleQuote = async (e) => {
    e.preventDefault();
    if (phone.length !== 10) return;
    setSubmitting(true);
    await submitLead({ type: `${category} Insurance`, name: 'Website Visitor', phone });
    setSubmitting(false);
    setSent(true);
    setTimeout(() => { setSent(false); setPhone(''); }, 5000);
  };

  return (
    <section className="relative overflow-hidden bg-gradient-to-br from-emerald-50 via-white to-teal-50">
      <div className="absolute inset-0 opacity-40 pointer-events-none">
        <div className="absolute top-10 -left-16 w-72 h-72 rounded-full bg-emerald-200/40 blur-3xl" />
        <div className="absolute bottom-10 -right-20 w-96 h-96 rounded-full bg-teal-200/40 blur-3xl" />
      </div>

      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 lg:py-24">
        <div className="grid lg:grid-cols-2 gap-12 items-center">
          <div>
            <div className="inline-flex items-center gap-2 bg-white/80 backdrop-blur border border-emerald-100 text-emerald-700 px-4 py-1.5 rounded-full text-sm font-medium mb-6">
              <Zap className="w-4 h-4" /> Trusted by 1000+ customers!
            </div>
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-[#0F2A4B] leading-[1.1] tracking-tight">
              Insurance,
              <br />
              <span className="text-[#00B48E]">Simplified.</span>
            </h1>
            <p className="mt-6 text-lg text-slate-600 max-w-lg leading-relaxed">
              Insurance • Investment • Insight — all in one place. Compare, buy & renew insurance from 42+ insurers across India with advisors you can trust.
            </p>

            <div className="mt-8 flex flex-wrap gap-3">
              <Button className="bg-[#00B48E] hover:bg-[#00a080] text-white rounded-full px-7 py-6 text-base font-semibold shadow-md shadow-emerald-200">
                Get Free Quote
              </Button>
              <a href="#contact">
                <Button variant="outline" className="rounded-full px-7 py-6 text-base font-semibold border-slate-300 text-slate-700 hover:bg-slate-50">
                  Talk to Expert
                </Button>
              </a>
            </div>

            <div className="mt-8 flex items-center gap-6 text-sm text-slate-600">
              <div className="flex items-center gap-2"><CheckCircle2 className="w-4 h-4 text-emerald-600" /> IRDAI Licensed</div>
              <div className="flex items-center gap-2"><CheckCircle2 className="w-4 h-4 text-emerald-600" /> Instant Quotes</div>
              <div className="flex items-center gap-2"><CheckCircle2 className="w-4 h-4 text-emerald-600" /> Claims Support</div>
            </div>
          </div>

          <div className="relative">
            <div className="relative bg-white rounded-3xl shadow-xl border border-slate-100 p-6 sm:p-8">
              <div className="flex items-center justify-between mb-5">
                <div className="flex items-center gap-2">
                  <div className="w-10 h-10 rounded-xl bg-emerald-100 flex items-center justify-center">
                    <ShieldCheck className="w-5 h-5 text-[#00B48E]" />
                  </div>
                  <div>
                    <div className="text-sm font-semibold text-slate-900">Quick Quote</div>
                    <div className="text-xs text-slate-500">Compare in 30 seconds</div>
                  </div>
                </div>
                <span className="text-xs bg-emerald-50 text-emerald-700 px-3 py-1 rounded-full font-medium">Live</span>
              </div>

              <div className="grid grid-cols-2 gap-3 mb-5">
                {['Health', 'Car', 'Bike', 'Life'].map((c) => (
                  <button
                    key={c}
                    type="button"
                    onClick={() => setCategory(c)}
                    className={`px-4 py-3 rounded-xl border text-sm font-medium transition-all ${
                      category === c
                        ? 'border-[#00B48E] bg-emerald-50 text-[#00B48E]'
                        : 'border-slate-200 text-slate-700 hover:border-[#00B48E] hover:bg-emerald-50'
                    }`}
                  >
                    {c}
                  </button>
                ))}
              </div>

              {sent ? (
                <div className="py-8 text-center">
                  <div className="w-14 h-14 rounded-full bg-emerald-100 flex items-center justify-center mx-auto mb-3">
                    <CheckCircle2 className="w-8 h-8 text-[#00B48E]" />
                  </div>
                  <div className="text-base font-bold text-[#0F2A4B]">Quote request sent!</div>
                  <div className="text-sm text-slate-600 mt-1">We'll call you shortly on +91 {phone}</div>
                </div>
              ) : (
                <form onSubmit={handleQuote} className="space-y-3">
                  <div className="flex items-center border border-slate-200 rounded-xl overflow-hidden focus-within:border-[#00B48E]">
                    <span className="px-3 py-3 bg-slate-50 text-slate-700 text-sm font-medium border-r">+91</span>
                    <input
                      value={phone}
                      onChange={(e) => setPhone(e.target.value.replace(/\D/g, '').slice(0, 10))}
                      className="flex-1 px-3 py-3 text-sm outline-none"
                      placeholder="Enter your mobile number"
                      required
                    />
                  </div>
                  <Button
                    type="submit"
                    disabled={submitting || phone.length !== 10}
                    className="w-full bg-[#FF7A00] hover:bg-[#e66e00] text-white rounded-xl py-6 font-semibold disabled:opacity-60"
                  >
                    {submitting ? 'Sending...' : 'View Quotes'}
                  </Button>
                </form>
              )}

              <div className="mt-5 pt-5 border-t border-slate-100 grid grid-cols-3 gap-2">
                {stats.map((s) => (
                  <div key={s.label} className="text-center">
                    <div className="text-lg font-bold text-[#0F2A4B]">{s.value}</div>
                    <div className="text-[11px] text-slate-500">{s.label}</div>
                  </div>
                ))}
              </div>
            </div>

            <div className="absolute -top-4 -right-4 bg-white rounded-2xl shadow-lg border border-slate-100 p-3 hidden sm:flex items-center gap-2">
              <div className="w-8 h-8 rounded-lg bg-orange-100 flex items-center justify-center">
                <Zap className="w-4 h-4 text-orange-600" />
              </div>
              <div>
                <div className="text-xs font-semibold text-slate-900">Instant Issuance</div>
                <div className="text-[10px] text-slate-500">Policy in 5 mins</div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Hero;
